import React, { useContext, useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { AppState } from "../../store/app-state";
import { ButtonPrimary, ButtonSecondary } from "../form/button/button.styled";
import Input from "../form/Input/input";
import RangeInput from "../form/range-input/range-input";
import SelectTag from "../form/select-tag/select-tag";
import Select from "../form/select/select";
import {
  workTypeOptions,
} from "./constants";
import { JobOfferContext } from "./context";
import {
  Buttons,
  EditField,
  EditInfos,
  Footer,
  Range,
  SkillEdit,
} from "./joboffer.styled";
import { convertIdNameToLabelValue } from "../../utils/redux/interface-converter";
import { commonApi } from "../../api/talent/common-service";
import { DEFAULT_CURRENCIES, DEFAULT_LANGUAGES } from "../../utils/constants";
import TextArea from "../form/text-area";

interface JobOfferFormProps {
  dismiss: () => void;
}

const JobOfferForm: React.FC<JobOfferFormProps> = ({ dismiss }) => {
  const { t } = useTranslation("private-home-page");
  const [workplace, setWorkplace] = useState<string>("");

  const { jobofferform, updateJobOffer, handleFormChange, onResetJobOffer } =
    useContext(JobOfferContext);

  const handleUpdate = () => {
    updateJobOffer();
    dismiss();
  };

  const { data: ecosystemsData } = useSelector(
    (state: AppState) => state.candidateState.candidateEcosystemsData
  );

  const { data: hireTypeData } = useSelector(
    (store: AppState) => store.commonState.hireTypeData
  );

  const { data: languageLevelData } = useSelector(
    (store: AppState) => store.commonState.proficiencyData
  );

  const languages = useMemo(() => {
    return DEFAULT_LANGUAGES;
  }, []);
  
  const currencies = useMemo(() => {
    return DEFAULT_CURRENCIES;
  }, []);

  const handleDismiss = () => {
    onResetJobOffer();
    dismiss();
  };

  const ecosystems = useMemo(() => {
    return ecosystemsData?.filter(
      (ecosystem, index, self) =>
        index === self.findIndex((t) => t.name === ecosystem.name)
    );
  }, [ecosystemsData]);

  const hireTypesOptions = useMemo(() => {
    return hireTypeData?.map((h) => convertIdNameToLabelValue(h));
  }, [hireTypeData]);

  const languageLevelOptions = useMemo(() => {
    return languageLevelData?.map((l) => convertIdNameToLabelValue(l));
  }, [languageLevelData]);

  const languageLevel = useMemo(() => {
    const l = jobofferform?.language_level
      ? [...jobofferform.language_level]
      : [];
    return l.reverse()[0];
  }, [jobofferform?.language_level]);
  
  useEffect(() => {
    if(jobofferform?.workplace){
      const id = parseInt(jobofferform?.workplace);
      if(!isNaN(id)){
        commonApi.fetchCityById(id).then((res) => {
          setWorkplace(res.data.label);
        });
      } else {
        setWorkplace(jobofferform?.workplace);
      }
    }
  }, [jobofferform?.workplace, setWorkplace]);

  const handleLanguageLevelChange = useCallback((languageLevel) => {
    if (!languageLevel) {
      return handleFormChange({ language_level: [] });
    }
    const selectedLanguages = [];
    for(let l of languageLevelData) {
      selectedLanguages.push(l);
      if(l.id === languageLevel.value){
        break;
      }
    }
    handleFormChange({ language_level: selectedLanguages.map(convertIdNameToLabelValue) });
  }, [languageLevelData, handleFormChange]);

  const handleWorkplaceChange = useCallback((workplace) => {
    if (!workplace) {
      return;
    }
    const id = parseInt(workplace);
    let workplaceValue = workplace;
    if(!isNaN(id)){
      commonApi.fetchCityById(id).then((res) => {
        workplaceValue = res.data.label;
      });
    }
    setWorkplace(workplaceValue);
    handleFormChange({ workplace: workplaceValue });
  }, [handleFormChange, setWorkplace]);

  const handlePlusInfoChange = useCallback((plusInfo?: string) => {
    handleFormChange({ plusInfo });
  }, [handleFormChange]);

  return (
    <>
      <EditInfos>
        <SkillEdit>
          <p>{`${t("jobOffer.modal.coreTechnology")}`}</p>
          <Select
            placeholder={t("filterForm.select")}
            value={jobofferform?.ecosystem}
            values={ecosystems || []}
            onChange={(ecosystem) => handleFormChange({ ecosystem })}
            getElementLabel={(ecosystem) => ecosystem?.name || ""}
          />
        </SkillEdit>
        <EditField>
          <p>{t("jobOffer.modal.currency")}</p>
          <Select
            placeholder={t("filterForm.select")}
            value={jobofferform?.currency || currencies[0]}
            values={currencies}
            onChange={(currency) => handleFormChange({ currency })}
            onClear={() => handleFormChange({ currency: currencies[0] })}
            getElementLabel={(c) => `${c.value} (${c.label})`}
          />
        </EditField>
        <EditField>
          <p>{t("jobOffer.modal.salary")} </p>
          <Range>
            <RangeInput
              placeholder={"00.00"}
              value={jobofferform?.salary}
              debounceDelay={200}
              onDeboncedValueChange={(salary) =>
                handleFormChange({ salary })
              }
            />
          </Range>
        </EditField>
        <EditField>
          <p>{t("jobOffer.modal.hireType")}</p>
          <SelectTag
            placeholder={t("filterForm.select")}
            values={jobofferform?.hireTypes}
            options={hireTypesOptions}
            onChange={(hireTypes) => handleFormChange({ hireTypes })}
          />
        </EditField>
        <EditField>
          <p>{t("jobOffer.modal.workType")} </p>
          <SelectTag
            placeholder={t("filterForm.select")}
            values={jobofferform?.workType}
            options={workTypeOptions.map((workOption) => ({
              value: workOption.value,
              label: t(workOption.label),
            }))}
            onChange={(workType) => handleFormChange({ workType })}
          />
        </EditField>
        <EditField>
          <p>{t("jobOffer.modal.language")} </p>
          <Select
            placeholder={t("filterForm.select")}
            value={jobofferform?.language}
            values={languages}
            onChange={(language) => handleFormChange({ language })}
            getElementLabel={(lang) => lang?.label ? t(lang.label) : ""}
            onClear={() => {handleFormChange({ language: undefined, language_level: [] })}}
          />
        </EditField>
       {!!jobofferform?.language && ( 
        <EditField>
            <p>{t("jobOffer.modal.language_level")} </p>
            <Select
              placeholder={t("filterForm.select")}
              value={languageLevel}
              values={languageLevelOptions}
              onChange={handleLanguageLevelChange}
              getElementLabel={(lang) => lang?.label ? t(lang.label) : ""}
              onClear={() => handleLanguageLevelChange(undefined)}
            />
          </EditField>
        )}
        <EditField>
          <p>{t("jobOffer.modal.city")}</p>
          <Input
            name="workplace"
            type="text"
            placeholder={t("filterForm.city")}
            value={workplace}
            onValueChange={handleWorkplaceChange}
          />
        </EditField>
        <EditField style={{alignItems: 'flex-start'}}>
          <p>{t("jobOffer.modal.plus_info")}</p>
          <TextArea
            name="plusInfo"
            placeholder={t("filterForm.plus_info")}
            value={jobofferform?.plusInfo}
            onValueChange={handlePlusInfoChange}
            maxLength={200}
          />
        </EditField>
      </EditInfos>
      <Footer>
        <Buttons>
          <ButtonSecondary onClick={() => handleDismiss()}>
            {t("jobOffer.buttons.discard")}
          </ButtonSecondary>
          <ButtonPrimary onClick={() => handleUpdate()}>
            {t("jobOffer.buttons.save")}
          </ButtonPrimary>
        </Buttons>
      </Footer>
    </>
  );
};

export default JobOfferForm;
